import { AddRounded, CloseRounded, WarningAmberRounded } from '@mui/icons-material';
import { Box, Switch, Tooltip } from '@mui/material';
import { FC, MouseEvent } from 'react';
import ReactGA from 'react-ga4';
import { SCREEN_NAMES } from 'src/constants/screenNames';
import { DispatchCard, Vehicle } from 'src/entities/dispatch';

import { DisplayMetric, ValueMetric } from './DisplayMetricToggle';

const VALUE_METRIC_KEYS = ['quantity', 'weight', 'volume'] as const satisfies readonly ValueMetric[];
const isValueMetric = (m: DisplayMetric): m is ValueMetric => (VALUE_METRIC_KEYS as readonly string[]).includes(m);

const integerFormatter = new Intl.NumberFormat('ja-JP');
const decimalFormatter = new Intl.NumberFormat('ja-JP', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

interface VehicleInfoProps {
  vehicle: Vehicle;
  cards: DispatchCard[];
  rotationNumber: number;
  enabled: boolean;
  driverName?: string;
  displayMetrics?: Set<DisplayMetric>;
  /** この rotation が最終回転の場合 true。回転追加ボタンを表示 */
  canAddRound?: boolean;
  /** 2回転目以降で true。削除ボタンを表示 */
  canDeleteRound?: boolean;
  onToggleEnabled?: () => void;
  onAddRound?: () => void;
  onDeleteRound?: () => void;
  /** 車両名 (truck) クリックで select menu を開く */
  onTruckClick?: (anchor: HTMLElement) => void;
  /** 車番クリックで編集する */
  onPlateNumberClick?: (anchor: HTMLElement) => void;
  /** ドライバー名クリックで select menu を開く */
  onDriverClick?: (anchor: HTMLElement) => void;
}

type LoadTotals = { cases: number; kg: number; m3: number };

function getActualLoad(cards: DispatchCard[]): LoadTotals {
  return cards.reduce<LoadTotals>(
    (acc, card) => {
      if (card.type !== 'delivery' || card.status !== 'matched') return acc;
      acc.cases += card.todayCases ?? 0;
      acc.kg += card.weight ?? 0;
      acc.m3 += card.volume ?? 0;
      return acc;
    },
    { cases: 0, kg: 0, m3: 0 },
  );
}

export const VehicleInfoView: FC<VehicleInfoProps> = ({
  vehicle,
  cards,
  rotationNumber,
  enabled,
  driverName,
  displayMetrics,
  canAddRound = false,
  canDeleteRound = false,
  onToggleEnabled,
  onAddRound,
  onDeleteRound,
  onTruckClick,
  onPlateNumberClick,
  onDriverClick,
}) => {
  const load = getActualLoad(cards);

  const effectiveMetrics = displayMetrics ?? new Set<DisplayMetric>(['quantity']);
  // 数値指標 (quantity / weight / volume) のみを抽出する。表示情報 (origin_dest 等) を末尾に
  // 入れると最終的な else 枝に落ちて volume 扱いされる不具合を防ぐため、ValueMetric だけで
  // activeMetric を決める。チェックされた ValueMetric の最後 (= 最後にトグルされたもの) を採用。
  const valueMetricsArray = Array.from(effectiveMetrics).filter(isValueMetric);
  const activeMetric: ValueMetric = valueMetricsArray.length > 0
    ? valueMetricsArray[valueMetricsArray.length - 1]
    : 'quantity';

  // メトリクスごとに実データで current / max を取得。truck マスタの値が無ければ null
  // (= キャパ不明扱い → 超過判定・利用率計算の対象外)。
  const maxCases = vehicle.maxCapacity;
  const maxKg = vehicle.maxCapacityKg ?? null;
  const maxM3 = vehicle.maxCapacityM3 ?? null;

  const currentValue =
    activeMetric === 'weight' ? load.kg : activeMetric === 'volume' ? load.m3 : load.cases;
  const maxValue: number | null = activeMetric === 'weight' ? maxKg : activeMetric === 'volume' ? maxM3 : maxCases;
  const isOverloaded = maxValue != null && maxValue > 0 && currentValue > maxValue;
  const utilization = maxValue != null && maxValue > 0 ? (currentValue / maxValue) * 100 : 0;
  const gaugeWidth = enabled ? Math.min(Math.max(utilization, 0), 100) : 0;
  const currentDriverName = driverName ?? vehicle.driverName ?? '';
  // ESLint typed rules が JSX 直下の truthy narrow を見落とすことがあるので
  // 事前に抜き出して narrow を確実に効かせる。
  const driverShift = vehicle.driverShift ?? null;

  // 超過時もカード全体は赤くしない (白文字との相性が悪く読みづらいため)。
  // 強調はバー (status-error-accent) と数字テキスト (status-error) と「超過」バッジで行う。
  const wrapperBg = !enabled
    ? 'hsl(var(--secondary) / 0.35)'
    : 'hsl(var(--surface-vehicle) / 0.55)';

  const stopPropagation = (e: MouseEvent) => e.stopPropagation();

  return (
    <Box
      sx={{
        height: 72,
        width: 288,
        flexShrink: 0,
        px: 1.5,
        py: 0.75,
        transition: 'background-color 200ms',
        bgcolor: wrapperBg,
        opacity: enabled ? 1 : 0.75,
      }}
    >
      <Box sx={{ display: 'flex', height: '100%', alignItems: 'flex-start', gap: 0.5 }}>
        <Box sx={{ minWidth: 0, flex: 1, '& > * + *': { mt: '2px' } }}>
          {/* 1段目: 運送会社 + 車両名 (車両名はクリックで車両選択メニューを開く)
              運送会社名が長い場合に車両名が押し潰されないよう、運送会社は最大 45% 幅で
              ellipsis 省略、車両名は残り幅 (flex 1 1 auto) を使う。両方 Tooltip で全文表示。 */}
          <Box sx={{ display: 'flex', minWidth: 0, alignItems: 'center', gap: 0.5, fontSize: 11, lineHeight: 1, pt: '2px' }}>
            {vehicle.companyName && (
              <Tooltip title={vehicle.companyName} placement="top" enterDelay={300}>
                <Box
                  component="span"
                  sx={{
                    flex: '0 1 auto',
                    minWidth: 0,
                    maxWidth: '45%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontWeight: 700,
                    letterSpacing: '-0.01em',
                    color: 'hsl(var(--foreground))',
                  }}
                >
                  {vehicle.companyName}
                </Box>
              </Tooltip>
            )}
            {vehicle.companyName && (
              <Box component="span" sx={{ flexShrink: 0, color: 'hsl(var(--muted-foreground))', opacity: 0.6 }}>/</Box>
            )}
            <Tooltip title={vehicle.truckName || vehicle.vehicleNumber} placement="top" enterDelay={300}>
              <Box
                component={onTruckClick ? 'button' : 'span'}
                type={onTruckClick ? 'button' : undefined}
                onClick={onTruckClick ? (e: MouseEvent<HTMLElement>) => {
                  stopPropagation(e);
                  ReactGA.event('click', {
                    screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
                    button_name: 'トラック変更',
                  });
                  onTruckClick(e.currentTarget);
                } : undefined}
                sx={{
                  flex: '1 1 auto',
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontWeight: vehicle.companyName ? 400 : 700,
                  color: 'hsl(var(--foreground))',
                  bgcolor: 'transparent',
                  border: 'none',
                  p: 0,
                  textAlign: 'left',
                  cursor: onTruckClick ? 'pointer' : 'default',
                  '&:hover': onTruckClick ? { textDecoration: 'underline' } : undefined,
                }}
              >
                {vehicle.truckName || vehicle.vehicleNumber}
              </Box>
            </Tooltip>
          </Box>

          {/* 2段目: 車番 + ドライバー名 (両方クリック可) */}
          <Box sx={{ display: 'flex', minWidth: 0, alignItems: 'center', gap: 1, fontSize: 10 }}>
            <Box
              component={onPlateNumberClick ? 'button' : 'span'}
              type={onPlateNumberClick ? 'button' : undefined}
              onClick={onPlateNumberClick ? (e: MouseEvent<HTMLElement>) => {
                stopPropagation(e);
                ReactGA.event('click', {
                  screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
                  button_name: '車番変更',
                });
                onPlateNumberClick(e.currentTarget);
              } : undefined}
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: vehicle.plateNumber ? 'hsl(var(--foreground))' : 'hsl(var(--muted-foreground) / 0.7)',
                bgcolor: 'transparent',
                border: 'none',
                p: 0,
                cursor: onPlateNumberClick ? 'pointer' : 'default',
                '&:hover': onPlateNumberClick ? { textDecoration: 'underline' } : undefined,
              }}
            >
              {vehicle.plateNumber || '車番未設定'}
            </Box>
            <Box
              component={onDriverClick ? 'button' : 'span'}
              type={onDriverClick ? 'button' : undefined}
              onClick={onDriverClick ? (e: MouseEvent<HTMLElement>) => {
                stopPropagation(e);
                ReactGA.event('click', {
                  screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
                  button_name: 'ドライバー変更',
                });
                onDriverClick(e.currentTarget);
              } : undefined}
              sx={{
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: currentDriverName ? 'hsl(var(--foreground))' : 'hsl(var(--muted-foreground) / 0.7)',
                bgcolor: 'transparent',
                border: 'none',
                p: 0,
                textAlign: 'left',
                cursor: onDriverClick ? 'pointer' : 'default',
                '&:hover': onDriverClick ? { textDecoration: 'underline' } : undefined,
              }}
            >
              {currentDriverName || 'ドライバー未設定'}
            </Box>
            {driverShift && (
              <Tooltip title={`勤務時間 ${driverShift.start} - ${driverShift.end}`} placement="top" enterDelay={300}>
                <Box
                  component="span"
                  sx={{
                    flexShrink: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: 10,
                    fontVariantNumeric: 'tabular-nums',
                    color: 'hsl(var(--muted-foreground))',
                  }}
                >
                  {driverShift.start}–{driverShift.end}
                </Box>
              </Tooltip>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, fontSize: 10, lineHeight: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box
                component="span"
                sx={{
                  fontFamily: 'monospace',
                  fontVariantNumeric: 'tabular-nums',
                  fontWeight: enabled && isOverloaded ? 700 : 400,
                  color: enabled && isOverloaded ? 'hsl(var(--status-error))' : 'hsl(var(--foreground))',
                }}
              >
                {activeMetric === 'quantity'
                  ? `${integerFormatter.format(load.cases)} / ${maxCases != null ? integerFormatter.format(maxCases) : '--'}cs`
                  : activeMetric === 'weight'
                    ? `${integerFormatter.format(Math.round(load.kg))} / ${maxKg != null ? integerFormatter.format(Math.round(maxKg)) : '--'}kg`
                    : `${decimalFormatter.format(load.m3)} / ${maxM3 != null ? decimalFormatter.format(maxM3) : '--'}m³`}
              </Box>
            </Box>
            {canAddRound && enabled && (
              <Box
                component="button"
                type="button"
                onClick={(e) => {
                  stopPropagation(e);
                  ReactGA.event('click', {
                    screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
                    button_name: '回転追加',
                  });
                  onAddRound?.();
                }}
                aria-label="回転を追加"
                sx={{
                  display: 'inline-flex',
                  height: 20,
                  alignItems: 'center',
                  gap: 0.5,
                  borderRadius: 1,
                  border: '1px dashed hsl(var(--input))',
                  px: 0.75,
                  fontSize: 10,
                  fontWeight: 600,
                  color: 'hsl(var(--muted-foreground))',
                  bgcolor: 'transparent',
                  cursor: 'pointer',
                  transition: 'background-color 150ms',
                  '&:hover': { bgcolor: 'hsl(var(--secondary))' },
                }}
              >
                <AddRounded sx={{ fontSize: 12 }} />
                回転追加
              </Box>
            )}
          </Box>

          <Box sx={{ height: 4, overflow: 'hidden', borderRadius: 999, bgcolor: 'hsl(var(--secondary))' }}>
            <Box
              sx={{
                height: '100%',
                borderRadius: 999,
                bgcolor: enabled
                  ? isOverloaded
                    ? 'hsl(var(--status-error-accent))'
                    : 'hsl(var(--primary))'
                  : 'hsl(var(--muted))',
                width: `${gaugeWidth}%`,
              }}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', height: '100%', width: 88, flexShrink: 0, flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', minHeight: 34, flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
            {enabled && isOverloaded && (
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.25,
                  fontSize: 10,
                  fontWeight: 600,
                  lineHeight: 1,
                  color: 'hsl(var(--status-error))',
                }}
              >
                <WarningAmberRounded sx={{ fontSize: 12 }} />
                超過
              </Box>
            )}
            {rotationNumber >= 2 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box
                  component="span"
                  sx={{
                    borderRadius: 0.5,
                    bgcolor: 'hsl(var(--accent))',
                    px: 0.75,
                    py: 0.25,
                    fontSize: 10,
                    fontWeight: 600,
                    lineHeight: 1,
                    color: 'hsl(var(--accent-foreground))',
                  }}
                >
                  {rotationNumber}回転目
                </Box>
                {canDeleteRound && enabled && (
                  <Box
                    component="button"
                    type="button"
                    onClick={(e) => {
                      stopPropagation(e);
                      ReactGA.event('click', {
                        screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
                        button_name: '回転削除',
                      });
                      onDeleteRound?.();
                    }}
                    aria-label="この回転を削除"
                    sx={{
                      display: 'inline-flex',
                      height: 16,
                      width: 16,
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 0.5,
                      border: 'none',
                      bgcolor: 'transparent',
                      color: 'hsl(var(--muted-foreground))',
                      cursor: 'pointer',
                      transition: 'background-color 150ms, color 150ms',
                      '&:hover': {
                        bgcolor: 'hsl(var(--destructive) / 0.1)',
                        color: 'hsl(var(--destructive))',
                      },
                    }}
                  >
                    <CloseRounded sx={{ fontSize: 12 }} />
                  </Box>
                )}
              </Box>
            )}
          </Box>

          <Tooltip
            title={enabled ? 'OFFにすると、この車両の配送はすべて未割当に戻ります' : '使用停止中'}
            placement="top"
            enterDelay={150}
          >
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
            <span
              onClick={stopPropagation}
              style={{ display: 'inline-flex', alignItems: 'center', lineHeight: 0 }}
            >
              <Switch
                size="small"
                checked={enabled}
                onChange={() => {
                  ReactGA.event('change', {
                    screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
                    button_name: '車両 使用切替',
                    label: enabled ? 'OFF' : 'ON',
                  });
                  onToggleEnabled?.();
                }}
                inputProps={{ 'aria-label': enabled ? '使用中' : '不使用' }}
                sx={{
                  m: 0,
                  p: 0,
                  width: 34,
                  height: 20,
                  display: 'flex',
                  overflow: 'visible',
                  '& .MuiSwitch-switchBase': {
                    p: 0,
                    m: '2px',
                    transitionDuration: '180ms',
                    '&.Mui-checked': {
                      transform: 'translateX(14px)',
                      color: '#fff',
                      '& + .MuiSwitch-track': { opacity: 1, bgcolor: 'hsl(var(--primary))', border: 'none' },
                      '& .MuiSwitch-thumb': { bgcolor: '#fff' },
                    },
                  },
                  '& .MuiSwitch-thumb': {
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    bgcolor: '#fff',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.4), 0 0 0 0.5px rgba(0,0,0,0.06)',
                  },
                  '& .MuiSwitch-track': {
                    borderRadius: 10,
                    opacity: 1,
                    bgcolor: 'hsl(var(--muted-foreground) / 0.4)',
                    border: '1px solid hsl(var(--border))',
                    boxSizing: 'border-box',
                    transition: 'background-color 180ms',
                  },
                }}
              />
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};
